import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { Link } from 'react-router';
import { Badge, Card, ErrorBanner, FilterSelect, PageHeader, Spinner, Table } from '../components/ui';
import { api } from '../lib/api';
import { useUrlState } from '../lib/hooks';
import { dateTime, inr } from '../lib/format';

interface Dash {
  range: { from: string; to: string; timezone: string };
  placedOrders: { count: number; valuePaise: number }; successfulPayments: { count: number; valuePaise: number }; refunds: { count: number; valuePaise: number };
  netRevenuePaise: number; averageOrderValuePaise: number;
  orders: { byStatus: Record<string, number>; pending: number; completed: number; cancelled: number };
  pendingPayments: { onlineAwaitingPayment: number; codAwaitingCollection: number }; failedPayments: number;
  customers: { total: number; new: number }; products: { active: number; lowStockVariants: number; outOfStockVariants: number };
  salesTrend: { day: string; orders: number; valuePaise: number }[]; topProducts: { productId: string; name: string; units: number; valuePaise: number }[];
  recentOrders: { id: string; orderNumber: string; status: string; paymentStatus: string; paymentMethod: string; totalPaise: number; placedAt: string }[];
  definitions: Record<string, string>;
}

const PRESETS = [{ value: 'today', label: 'Today' }, { value: 'yesterday', label: 'Yesterday' }, { value: 'last7', label: 'Last 7 days' }, { value: 'last30', label: 'Last 30 days' }, { value: 'thisMonth', label: 'This month' }];

function Stat({ label, value, sub, def }: { label: string; value: string; sub?: string; def?: string }) {
  return (
    <div className="rounded-lg border border-cream-border bg-cream-surface p-4">
      <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}{def && <span title={def} aria-label={def} className="cursor-help"><Info className="h-3.5 w-3.5" /></span>}
      </p>
      <p className="mt-1 font-heading text-3xl font-semibold text-forest-900">{value}</p>
      {sub && <p className="text-xs text-ink-muted">{sub}</p>}
    </div>
  );
}

/** Minimal accessible bar chart (no chart library): daily order value. */
function Trend({ data }: { data: Dash['salesTrend'] }) {
  if (!data.some((d) => d.valuePaise > 0)) return <p className="flex h-40 items-center justify-center text-sm text-ink-muted">No sales in this period.</p>;
  const max = Math.max(1, ...data.map((d) => d.valuePaise));
  const w = 100 / Math.max(1, data.length);
  return (
    <figure>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-40 w-full" role="img" aria-label="Daily order value">
        {data.map((d, i) => {
          const h = (d.valuePaise / max) * 36;
          return <rect key={d.day} x={i * w + w * 0.15} y={40 - h} width={w * 0.7} height={Math.max(h, 0.3)} rx={0.6} className="fill-forest-700"><title>{`${d.day}: ${d.orders} orders, ${inr(d.valuePaise)}`}</title></rect>;
        })}
      </svg>
      <figcaption className="mt-1 flex justify-between text-xs text-ink-muted"><span>{data[0]?.day}</span><span>{data.at(-1)?.day}</span></figcaption>
    </figure>
  );
}

export function Dashboard() {
  const [f, setF] = useUrlState({ preset: 'last7' });
  const q = useQuery({ queryKey: ['dashboard', f.preset], queryFn: () => api<Dash>('/admin/dashboard', { query: { preset: f.preset } }) });
  const d = q.data;
  return (
    <>
      <PageHeader title="Dashboard" subtitle={d ? `${d.range.from} → ${d.range.to} (${d.range.timezone})` : undefined} actions={<FilterSelect label="Date range" value={f.preset} onChange={(preset) => setF({ preset })} options={PRESETS} />} />
      {q.isLoading && <Spinner />}
      {q.error && <ErrorBanner error={q.error} onRetry={() => void q.refetch()} />}
      {d && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Orders placed" value={String(d.placedOrders.count)} sub={inr(d.placedOrders.valuePaise)} def={d.definitions.placedOrders} />
            <Stat label="Money received" value={inr(d.successfulPayments.valuePaise)} sub={`${d.successfulPayments.count} payments`} def={d.definitions.successfulPayments} />
            <Stat label="Net revenue" value={inr(d.netRevenuePaise)} sub={`Refunds ${inr(d.refunds.valuePaise)}`} def={d.definitions.netRevenue} />
            <Stat label="Avg order value" value={inr(d.averageOrderValuePaise)} def={d.definitions.averageOrderValue} />
            <Stat label="Awaiting payment" value={String(d.pendingPayments.onlineAwaitingPayment)} sub={`${d.pendingPayments.codAwaitingCollection} COD to collect`} def={d.definitions.pendingPayments} />
            <Stat label="Failed payments" value={String(d.failedPayments)} def={d.definitions.failedPayments} />
            <Stat label="Customers" value={String(d.customers.total)} sub={`${d.customers.new} new`} />
            <Stat label="Stock alerts" value={String(d.products.lowStockVariants + d.products.outOfStockVariants)} sub={`${d.products.outOfStockVariants} out · ${d.products.lowStockVariants} low · ${d.products.active} live products`} />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card title="Sales" className="lg:col-span-2"><Trend data={d.salesTrend} /></Card>
            <Card title="Top products">
              {d.topProducts.length ? <ol className="space-y-2 text-sm">{d.topProducts.map((t) => <li key={t.productId} className="flex justify-between gap-2"><Link className="truncate text-forest-800 hover:underline" to={`/products/${t.productId}`}>{t.name}</Link><span className="shrink-0 text-ink-muted">{t.units} · {inr(t.valuePaise)}</span></li>)}</ol> : <p className="text-sm text-ink-muted">No sales in this period.</p>}
            </Card>
          </div>
          <Card title="Recent orders" actions={<Link to="/orders" className="text-sm font-medium text-forest-800 hover:underline">All orders →</Link>}>
            <Table rows={d.recentOrders} rowKey={(o) => o.id} columns={[
              { header: 'Order', cell: (o) => <Link className="font-medium text-forest-800 hover:underline" to={`/orders/${o.id}`}>{o.orderNumber}</Link> },
              { header: 'Placed', cell: (o) => dateTime(o.placedAt) },
              { header: 'Status', cell: (o) => <Badge status={o.status} /> },
              { header: 'Payment', cell: (o) => <span className="flex gap-1"><Badge status={o.paymentStatus} /><Badge tone="grey">{o.paymentMethod === 'COD' ? 'COD' : 'Online'}</Badge></span> },
              { header: 'Total', cell: (o) => inr(o.totalPaise), className: 'text-right' },
            ]} />
          </Card>
        </div>
      )}
    </>
  );
}
